import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Add, AutoAwesome, Search, TravelExplore } from '@mui/icons-material';
import { DataGrid, GridColDef, deDE } from '@mui/x-data-grid';
import { useSearchGrundbuchblatt } from '../hooks/reactQuerys/GrundbuchblattQueries';
import { LoadingOverlay } from '../Clients/ClientsOverview';
import { GrundbuchblattEditDialog } from './GrundbuchblattEditDialog';
import { GrundbuchAuszugImportDialog } from './GrundbuchAuszugImportDialog';
import { EigentuemerRechercheDialog } from '../LegalTransaction/LandRegisterQuery/EigentuemerRechercheDialog';
import { useGetKiStatus } from '../hooks/reactQuerys/KiQueries';
import { kiReadsDocuments } from '../Interfaces/IKi';
import {
  ILegalTransactionRelationshipRealEstate,
  initRealEstate,
} from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';

/**
 * Übersicht aller Grundbuchblätter (Variante A) — analog zu Mandanten und
 * Vorgängen: lädt automatisch alle Blätter in eine Tabelle; die Suche
 * (Blattnummer, Adresse, Eigentümer, Gemarkung, Flur, Flurstück) filtert nur
 * ergänzend. Klick auf eine Zeile öffnet die Blatt-Detailansicht.
 */
export const GrundstueckeOverview: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [auszugOpen, setAuszugOpen] = useState(false);
  const [rechercheOpen, setRechercheOpen] = useState(false);
  const { kiStatus } = useGetKiStatus();
  const [rechercheRealEstate, setRechercheRealEstate] =
    useState<ILegalTransactionRelationshipRealEstate>(initRealEstate());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { grundbuchblattHits, isSearchingGrundbuchblatt } =
    useSearchGrundbuchblatt(debounced);

  const columns: GridColDef[] = [
    { field: 'Blattnummer', headerName: 'Blatt', flex: 0.7 },
    { field: 'Amtsgericht', headerName: 'Amtsgericht', flex: 1 },
    { field: 'Gemarkung', headerName: 'Gemarkung', flex: 1 },
    {
      field: 'FlurstueckAnzahl',
      headerName: 'Flurstücke',
      flex: 0.5,
      type: 'number',
      valueGetter: ({ row }) => row.FlurstueckAnzahl ?? 0,
    },
    {
      field: 'Adressen',
      headerName: 'Adressen',
      flex: 1.6,
      sortable: false,
      renderCell: ({ value }) => (value ? String(value) : '–'),
    },
    {
      field: 'Eigentuemer',
      headerName: 'Eigentümer',
      flex: 1.4,
      sortable: false,
      renderCell: ({ value }) => (value ? String(value) : '–'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 1 }}>
        <Typography variant='h4' sx={{ flexGrow: 1 }}>
          Grundbuch
        </Typography>
        <Button
          variant='outlined'
          startIcon={<TravelExplore />}
          onClick={() => {
            setRechercheRealEstate(initRealEstate());
            setRechercheOpen(true);
          }}
        >
          Über Eigentümerrecherche anlegen
        </Button>
        {/* Nur wenn die KI eingerichtet ist — ein Knopf, der bloß eine
            Fehlermeldung öffnet, ist schlechter als keiner. */}
        {kiReadsDocuments(kiStatus) && (
          <Button
            variant='outlined'
            startIcon={<AutoAwesome />}
            onClick={() => setAuszugOpen(true)}
          >
            Anlegen über Grundbuchauszug
          </Button>
        )}
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
        >
          Neues Blatt
        </Button>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Übersicht aller Grundbuchblätter. Klicken Sie eine Zeile an, um
        Adressen, Abrufe und Eigentümer zu sehen.
      </Typography>

      <TextField
        size='small'
        placeholder='Suchen: Blattnummer, Adresse, Eigentümer, Gemarkung, Flur, Flurstück …'
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        InputProps={{
          startAdornment: <Search fontSize='small' sx={{ mr: 1 }} />,
        }}
        sx={{ mb: 2, width: '100%', maxWidth: 560 }}
      />

      <DataGrid
        loading={isSearchingGrundbuchblatt}
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={grundbuchblattHits}
        columns={columns}
        getRowId={row => row.idGrundbuchblatt}
        pagination
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        slots={{ loadingOverlay: () => <LoadingOverlay /> }}
        autoHeight
        disableColumnMenu
        sx={{ cursor: 'pointer' }}
        onRowClick={({ row }) =>
          navigate(`/grundstuecke/${row.idGrundbuchblatt}`)
        }
      />

      <GrundbuchblattEditDialog
        open={createOpen}
        idGrundbuchblatt={null}
        onClose={() => setCreateOpen(false)}
        onSaved={gid => {
          setCreateOpen(false);
          if (gid > 0) navigate(`/grundstuecke/${gid}`);
        }}
      />

      <GrundbuchAuszugImportDialog
        open={auszugOpen}
        onClose={() => {
          setAuszugOpen(false);
          // Ein übernommenes Blatt soll in der Liste erscheinen, ohne dass
          // jemand neu lädt.
          void queryClient.invalidateQueries({
            queryKey: ['grundbuchblatt', 'search'],
          });
        }}
      />

      {/* Eigentümerrecherche ohne Vorgang — reine Anlage (kein legalTransaction,
          kein onBlaetterAssigned → Zuordnung deaktiviert). Nach Schließen wird
          die Trefferliste aktualisiert, damit neue Blätter sofort erscheinen. */}
      {rechercheOpen && (
        <EigentuemerRechercheDialog
          autoOpen
          hideTrigger
          realEstateObject={rechercheRealEstate}
          setRealEstateObject={setRechercheRealEstate}
          onClosed={() => {
            setRechercheOpen(false);
            void queryClient.invalidateQueries({
              queryKey: ['grundbuchblatt', 'search'],
            });
          }}
        />
      )}
    </Box>
  );
};
